// Pure accommodation extra-time helpers. Moved here from
// src/pages/users/accommodationTime.ts when PR #6691 unified the derivation:
// the users world (AccommodationDialog), the experiences world (init parking,
// monitor not-joined seeding) and the student list all consume it, and lib is
// the layer-law share point for cross-world code.

export interface AccommodationTimeFields {
  additional_time?: string;
  additional_time_type?: 'percentage' | 'minutes';
}

// THE single derivation of extra time (ms) from an accommodations record's
// additional_time intent. Used by every producer and preview — first-join init,
// the accommodation dialog's pending grant, the monitor's not-joined rows, and
// list displays — so a value seeded at init is always identical to one granted
// via the dialog. Pure derivation: the APPLIED value lives on
// user_info.extra_time and is owned by the grant flows.
//
// Ms-precise on purpose: percentages of short tests yield fractional minutes
// (10% of 5m = 30s); rounding to whole minutes here would drop or inflate them.
// A missing additional_time_type means minutes — every UI writes the type
// explicitly and defaults the unit to minutes.
export const getExtraTimeMs = (
  accommodations: AccommodationTimeFields | undefined,
  experienceDurationMinutes: number
): number => {
  const value = parseInt(accommodations?.additional_time || '0') || 0;
  if (accommodations?.additional_time_type === 'percentage') {
    return Math.round(((experienceDurationMinutes || 0) * 60000 * value) / 100);
  }
  return value * 60000;
};

// Format milliseconds as "Xh Ym Zs", omitting any zero unit — the "New total
// duration" row can be fractional-minute when % applies to short tests.
export const formatDurationMs = (ms: number): string => {
  if (ms <= 0) return '0m';
  const totalSeconds = Math.round(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (seconds > 0) parts.push(`${seconds}s`);
  return parts.join(' ') || '0m';
};
